import type { SalesHierarchySalesHierarchyLinkSalesHierarchyLinkTreeReadModel } from '@r/api/generated/client.schemas.ts';

export interface HierarchyFlowNode {
  /** `uniqueCode` de la personne — seul identifiant stable exposé par l'arbre. */
  id: string;
  parentId: string | null;
  label: string;
}

export interface HierarchyFlowLink {
  /** Id du lien back (`SalesHierarchyLink`), utilisé pour le DELETE. */
  linkId: number;
  source: string;
  target: string;
}

/**
 * Aplatit l'arbre renvoyé par `GET /admin/sales-hierarchies/{id}/links` en
 * nœuds + liens exploitables pour le layout et le canvas. Chaque entrée de
 * l'arbre représente une personne (`sales`), pas un lien abstrait : `id` de
 * l'entrée = id du lien entrant (celui qui l'attache à son parent), `null`
 * pour une tête. Format confirmé par un payload réel du back, pas déduit du
 * schema seul (`sales.uniqueCode`/`sales.user.fullname`, pas d'id numérique
 * de UserWorkspace disponible ici).
 */
export function buildHierarchyFlowGraph(
  roots: readonly SalesHierarchySalesHierarchyLinkSalesHierarchyLinkTreeReadModel[]
): { nodes: HierarchyFlowNode[]; links: HierarchyFlowLink[] } {
  const nodes: HierarchyFlowNode[] = [];
  const links: HierarchyFlowLink[] = [];

  function visit(
    entry: SalesHierarchySalesHierarchyLinkSalesHierarchyLinkTreeReadModel,
    parentId: string | null
  ) {
    const uniqueCode = entry.sales?.uniqueCode;
    if (!uniqueCode) return; // défensif : entrée sans personne rattachée (données incohérentes)

    nodes.push({
      id: uniqueCode,
      parentId,
      label: entry.sales?.user?.fullname ?? uniqueCode
    });

    // ⚠️ Le schema généré déclare `id: number` (jamais null), mais le payload
    // réel confirmé par le back renvoie `id: null` pour une tête (pas de
    // lien entrant) — le schema OpenAPI est inexact sur ce point, on code
    // contre la réalité observée, pas contre le type généré.
    const linkId = entry.id as number | null;
    if (parentId !== null && linkId !== null) {
      links.push({ linkId, source: parentId, target: uniqueCode });
    }

    for (const subEntry of entry.subLinks ?? []) {
      visit(
        subEntry as SalesHierarchySalesHierarchyLinkSalesHierarchyLinkTreeReadModel,
        uniqueCode
      );
    }
  }

  for (const root of roots) {
    visit(root, null);
  }

  return { nodes, links };
}
